import React, { useEffect, useState, useCallback } from "react";
import apiClient from "../../api/apiClient";
import { getColor } from "../../utils/colorfunctions";
import { formatWeightFromGrams, toGrams } from "../../utils/weightUtils";
import { mixDisplayName } from "../../utils/mixDisplayName";
import { useLicense } from "../../context/licenseContext";
import { useLocation_ } from "../../context/locationContext";
import "./ShowroomProductModal.css";
import { GrowingDetailsDisplay } from "../ProductGrowingDetails/ProductGrowingDetails";
import { totalThc, aromaForColor, demsRatio, potencyTier, POTENCY_TIERS } from "../../utils/menu/menuMetrics";

const REQUEST_UNITS = ["g", "oz", "lb"];

const ShowroomProductModal = ({ item: initialItem, onClose }) => {
    const [productDetails, setProductDetails] = useState(null);
    const item = { ...initialItem, ...(productDetails || {}) };
    const { currentLicense } = useLicense();
    const { currentLocation } = useLocation_();
  
    const [contactEmail, setContactEmail] = useState(null);
    const [emailLoading, setEmailLoading] = useState(true);
    const [imageIndex, setImageIndex] = useState(0);

    // Data captured from the showroom item fetch 
    const [sellerLicenseNo, setSellerLicenseNo] = useState(null);
    const [sellerProductId, setSellerProductId] = useState(null); 

    // Transfer request form state
    const [requestWeight, setRequestWeight] = useState("");
    const [requestUnit, setRequestUnit] = useState("g");
    const [requestStatus, setRequestStatus] = useState("idle"); // idle | loading | success | error
    const [requestError, setRequestError] = useState("");

    // Build deduped image array from all three image sources (same logic as shoroomItem.js)
    const imageArray = (() => {
        const main = item.main_picture || "";
        const additional = item.additional_pictures
            ? item.additional_pictures.split(",").map((s) => s.trim()).filter(Boolean)
            : [];
        const coa = item.coa_pictures
            ? item.coa_pictures.split(",").map((s) => s.trim()).filter(Boolean)
            : [];
        const seen = new Set();
        return [main, ...additional, ...coa].filter((url) => {
            if (!url || seen.has(url)) return false;
            seen.add(url);
            return true;
        });
    })();

    useEffect(() => {
        let cancelled = false;
        setProductDetails(null);
        setSellerLicenseNo(null);
        setSellerProductId(null);
        setEmailLoading(true);
        setImageIndex(0);
        apiClient
            .get(`/showroom/get-showroom-item/${item.item_id}`)
            .then((res) => {
                if (cancelled) return;
                const data = res.data?.[0] || res.data;
                setProductDetails(data);
                setContactEmail(data?.contact_email || null);
                setSellerLicenseNo(data?.licenseNo || null);
                setSellerProductId(data?.product_id || null);
            })
            .catch(() => { if (!cancelled) setContactEmail(null); })
            .finally(() => { if (!cancelled) setEmailLoading(false); });
        return () => { cancelled = true; };
    }, [item.item_id]);

    const handleEscape = useCallback(
        (e) => { if (e.key === "Escape") onClose(); },
        [onClose]
    );

    useEffect(() => {
        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [handleEscape]);

    const isOwnLicense = sellerLicenseNo && currentLicense?.licenseNo
        && sellerLicenseNo === currentLicense.licenseNo;

    const quantityDisplay = item.product_weight_unit === "units"
        ? (item.product_quantity != null ? `${item.product_quantity} units` : "—")
        : formatWeightFromGrams(item.product_quantity);

    const PRICE_UNIT_LABELS = { g: "/ g", oz: "/ oz", lb: "/ lb", unit: "/ unit", mg_thc: "/ mg THC" };
    const priceUnit = item.price_unit ? PRICE_UNIT_LABELS[item.price_unit] || "" : "";

    const productDemsRatio = productDetails ? demsRatio(productDetails) : null;
    const productPotency = productDetails ? potencyTier(productDetails) : null;

    const handleRequestSubmit = async () => {
        const weightNum = parseFloat(requestWeight);
        if (isNaN(weightNum) || weightNum <= 0) {
            setRequestError("Enter a weight greater than 0.");
            return;
        }

        if (!sellerLicenseNo || !sellerProductId) {
            setRequestError("Seller information is still loading. Please try again.");
            return;
        }

        if (!currentLicense?.licenseNo) {
            setRequestError("No active license selected. Please select a license first.");
            return;
        }

        const availableQty = parseFloat(item.product_quantity) || 0;
        const requestedGrams = requestUnit === "units" ? weightNum : toGrams(weightNum, requestUnit);
        if (requestedGrams > availableQty) {
            setRequestError(`Requested amount exceeds available inventory (${formatWeightFromGrams(availableQty)}).`);
            return;
        }

        setRequestStatus("loading");
        setRequestError("");

        try {
            await apiClient.post("/transfer-requests", {
                product_id:            sellerProductId,
                showroom_item_id:      item.item_id,
                seller_license_no:     sellerLicenseNo,
                requester_license_no:  currentLicense.licenseNo,
                requester_location_id: currentLocation?.locationId ?? null,
                weight:                weightNum,
                weight_unit:           requestUnit,
            });
            setRequestStatus("success");
        } catch (err) {
            setRequestError(err.response?.data?.error || "Request failed. Please try again.");
            setRequestStatus("idle");
        }
    };

    return (
        <div className="spm__overlay" onClick={onClose}>
            <div className="spm__panel" onClick={(e) => e.stopPropagation()}>

                {/* HEADER */}
                <div className="spm__header">
                    <div className="spm__header-left">
                        {item.color && item.color !== "no color" && ((item.product_type || "").toLowerCase() === "flower" || (item.product_type || "").toLowerCase() === "pre-roll") && (
                            <span
                                className="spm__color-dot"
                                style={{ backgroundColor: getColor(item.color) }}
                            />
                        )}
                        <div>
                            <p className="spm__company">{item.company}</p>
                            <h2 className="spm__product-name">{mixDisplayName(item.product_name, item.product_type, item.created_at)}</h2>
                        </div>
                    </div>
                    <button className="spm__close" onClick={onClose} aria-label="Close">✕</button>
                </div>

                <div className="spm__body">
                    {/* IMAGE CAROUSEL */}
                    {imageArray.length > 0 ? (
                        <div className="spm__gallery">
                            <img
                                className="spm__gallery-img"
                                src={imageArray[imageIndex]}
                                alt={`Product image ${imageIndex + 1}`}
                            />
                            {imageArray.length > 1 && (
                                <div className="spm__gallery-controls">
                                    <button
                                        className="spm__gallery-btn"
                                        onClick={() =>
                                            setImageIndex((i) => (i - 1 + imageArray.length) % imageArray.length)
                                        }
                                        aria-label="Previous image"
                                    >
                                        ‹
                                    </button>
                                    <span className="spm__gallery-count">
                                        {imageIndex + 1} / {imageArray.length}
                                    </span>
                                    <button
                                        className="spm__gallery-btn"
                                        onClick={() =>
                                            setImageIndex((i) => (i + 1) % imageArray.length)
                                        }
                                        aria-label="Next image"
                                    >
                                        ›
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="spm__no-image">No images available</div>
                    )}

                    {productDetails && <section aria-label="Growing and product characteristics">
                        <h3>Growing &amp; product characteristics</h3>
                        <GrowingDetailsDisplay details={productDetails} options={productDetails.growing_options} />
                        <p className="pgd__note">Growing details and aroma descriptions are supplied by the grower.</p>
                        <dl className="pgd__values">
                            {productDetails.cannabinoid_type && <div><dt>Cannabinoid type</dt><dd>{productDetails.cannabinoid_type}</dd></div>}
                            {productDetails.color && productDetails.color !== "no color" && <>
                                <div><dt>DYC terpene color</dt><dd>{productDetails.color}</dd></div>
                                <div><dt>DYC aroma</dt><dd>{aromaForColor(productDetails.color) || "Unavailable"}</dd></div>
                            </>}
                            {totalThc(productDetails) != null && <div><dt>Total THC</dt><dd>{totalThc(productDetails)}%</dd></div>}
                            {productPotency && <div><dt>THC strength</dt><dd>{POTENCY_TIERS[productPotency].label}</dd></div>}
                            {productDemsRatio != null && <div><dt>DEMS ratio</dt><dd>{productDemsRatio.toFixed(4)}</dd></div>}
                        </dl>
                    </section>}

                    {/* DETAILS */}
                    <div className="spm__details">
                        <div className="spm__detail-row">
                            <span className="spm__detail-label">Price</span>
                            <span className="spm__detail-value">
                                ${parseFloat(item.price || 0).toFixed(2)}
                                <span className="spm__price-unit"> {priceUnit}</span>
                            </span>
                        </div>

                        <div className="spm__detail-row">
                            <span className="spm__detail-label">Quantity</span>
                            <span className="spm__detail-value">{quantityDisplay}</span>
                        </div>

                        {/* REQUEST PRODUCT SECTION */}
                        <div className="spm__request-section" data-tutorial="showroom-request-form">
                            <span className="spm__request-label">Request Product</span>

                            {isOwnLicense ? (
                                <p className="spm__request-own">
                                    This product belongs to your active license.
                                </p>
                            ) : requestStatus === "success" ? (
                                <div className="spm__request-success">
                                    ✓ Request sent! The seller will be notified.
                                </div>
                            ) : (
                                <>
                                    <div className="spm__request-row">
                                        <input
                                            type="number"
                                            className="spm__request-input"
                                            value={requestWeight}
                                            onChange={(e) => {
                                                setRequestWeight(e.target.value);
                                                setRequestError("");
                                            }}
                                            placeholder="0.00"
                                            min="0.01"
                                            step="0.01"
                                            disabled={requestStatus === "loading"}
                                        />
                                        <select
                                            className="spm__request-unit"
                                            value={requestUnit}
                                            onChange={(e) => setRequestUnit(e.target.value)}
                                            disabled={requestStatus === "loading"}
                                        >
                                            {REQUEST_UNITS.map((u) => (
                                                <option key={u} value={u}>{u}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {requestError && (
                                        <p className="spm__request-error">{requestError}</p>
                                    )}

                                    <button
                                        className={`spm__request-btn${requestStatus === "loading" ? " spm__request-btn--loading" : ""}`}
                                        onClick={handleRequestSubmit}
                                        disabled={requestStatus === "loading"}
                                    >
                                        {requestStatus === "loading" ? "Requesting…" : "Request Product"}
                                    </button>
                                </>
                            )}
                        </div>

                        <div className="spm__detail-row">
                            <span className="spm__detail-label">Contact</span>
                            {emailLoading ? (
                                <span className="spm__email-loading">Loading…</span>
                            ) : contactEmail ? (
                                <a className="spm__email" href={`mailto:${contactEmail}`}>
                                    {contactEmail}
                                </a>
                            ) : (
                                <span className="spm__detail-value spm__detail-value--muted">
                                    Not provided
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ShowroomProductModal;
